import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../../store/appStore';
import { client } from '../../api/client';
import { useI18n } from '../../i18n';
import logoImg from '../../assets/logo.png';

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, reset, notifications, unreadCount, theme, toggleTheme, tokenBalance, markNotificationRead, setNotifications } = useAppStore();
  const { t, language, setLanguage } = useI18n();

  // Load notifications from backend
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await client.api.fetch('/api/notifications');
      if (res.ok) {
        const data = await res.json();
        const mapped = (data.data || []).map((n: any) => ({
          id: String(n.id),
          userId: n.userId,
          type: n.type,
          title: n.title,
          message: n.message,
          link: n.link,
          read: !!n.isRead,
          createdAt: new Date(n.createdAt * 1000).toISOString(),
        }));
        setNotifications(mapped);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }, [isAuthenticated, setNotifications]);

  useEffect(() => {
    loadNotifications();
    // Reload notifications every 60 seconds
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleLogout = async () => {
    await client.auth.signOut();
    reset();
  };

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    // Mark notification as read
    if (!notification.read) {
      markNotificationRead(notification.id);
    }
    // Navigate to notification link or notifications page
    setNotificationOpen(false);
    if (notification.link) {
      navigate(notification.link);
    } else {
      navigate('/notifikacije');
    }
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navLinks = [
    { path: '/ponuda', label: t.nav.offers },
    { path: '/potraznja', label: t.nav.demands },
    { path: '/aukcije', label: t.nav.auctions },
    { path: '/blog', label: t.nav.blog },
    { path: '/cesta-pitanja', label: t.nav.faq },
    { path: '/o-nama', label: t.nav.about },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bg-slate-900 dark:bg-slate-950 text-white sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src={logoImg} alt="Svoj Na Svom" className="h-10 w-10" />
            <span className="font-bold text-lg hidden sm:block">Svoj Na Svom</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                  isActive(link.path)
                    ? 'bg-amber-500 text-slate-900 font-semibold'
                    : 'hover:bg-slate-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Language Toggle - hidden on small mobile when logged in */}
            <button
              onClick={() => setLanguage(language === 'sr' ? 'en' : 'sr')}
              className={`p-2 hover:bg-slate-800 rounded-lg transition-colors text-sm font-medium ${isAuthenticated ? 'hidden sm:block' : ''}`}
              title={t.common.language}
            >
              {language === 'sr' ? 'EN' : 'SR'}
            </button>

            {/* Theme Toggle - hidden on small mobile when logged in */}
            <button
              onClick={toggleTheme}
              className={`p-2 hover:bg-slate-800 rounded-lg transition-colors ${isAuthenticated ? 'hidden sm:block' : ''}`}
              title={theme === 'light' ? t.common.darkMode : t.common.lightMode}
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>

            {isAuthenticated ? (
              <>
                {/* Tokens - visible on all screen sizes */}
                <Link
                  to="/kupi-tokene"
                  className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors"
                  title={language === 'sr' ? 'Kupi tokene' : 'Buy tokens'}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                  </svg>
                  <span className="font-medium">{tokenBalance}</span>
                </Link>

                {/* Favorites */}
                <Link
                  to="/omiljeni"
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  title={language === 'sr' ? 'Omiljeni oglasi' : 'Favorite listings'}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </Link>

                {/* Notifications */}
                <div className="relative">
                  <button
                    onClick={() => setNotificationOpen(!notificationOpen)}
                    className="p-2 hover:bg-slate-800 rounded-lg relative"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {notificationOpen && (
                      <>
                        {/* Backdrop for mobile */}
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setNotificationOpen(false)}
                        />
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="fixed left-4 right-4 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-xl overflow-hidden z-50"
                          style={{ maxHeight: 'calc(100vh - 100px)' }}
                        >
                          <div className="p-3 bg-slate-100 dark:bg-slate-700 font-semibold border-b dark:border-slate-600">
                            {t.notifications.title}
                          </div>
                          <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                            {notifications.length === 0 ? (
                              <p className="p-4 text-slate-500 text-center">{t.notifications.noNotifications}</p>
                            ) : (
                              notifications.slice(0, 5).map((n) => (
                                <div
                                  key={n.id}
                                  onClick={() => handleNotificationClick(n)}
                                  className={`p-3 border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${!n.read ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                                >
                                  <p className="font-medium text-sm line-clamp-2">{n.title}</p>
                                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{n.message}</p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    {new Date(n.createdAt).toLocaleDateString(language === 'sr' ? 'sr-RS' : 'en-US')}
                                  </p>
                                </div>
                              ))
                            )}
                          </div>
                          {notifications.length > 0 && (
                            <Link
                              to="/notifikacije"
                              onClick={() => setNotificationOpen(false)}
                              className="block p-3 text-center text-amber-600 hover:bg-slate-50 dark:hover:bg-slate-700 font-medium text-sm border-t dark:border-slate-700"
                            >
                              {language === 'sr' ? 'Pogledaj sve notifikacije' : 'View all notifications'}
                            </Link>
                          )}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-2 hover:bg-slate-800 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-slate-900 font-bold">
                      {user?.name?.charAt(0) || 'K'}
                    </div>
                    <span className="hidden sm:block">{user?.name}</span>
                  </button>
                  
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-xl overflow-hidden"
                      >
                        <Link
                          to="/profil"
                          className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {t.nav.profile}
                        </Link>
                        <Link
                          to="/moji-oglasi"
                          className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {t.nav.myListings}
                        </Link>
                        <Link
                          to="/novi-oglas"
                          className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {t.nav.newListing}
                        </Link>
                        <Link
                          to="/moje-pretrage"
                          className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {language === 'sr' ? 'Moje pretrage' : 'My Searches'}
                        </Link>
                        <Link
                          to="/kalendar"
                          className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {language === 'sr' ? 'Kalendar dostupnosti' : 'Availability Calendar'}
                        </Link>
                        <Link
                          to="/notifikacije"
                          className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          {language === 'sr' ? 'Notifikacije' : 'Notifications'}
                        </Link>
                        {user?.role === 'admin' && (
                          <Link
                            to="/admin"
                            className="block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 text-amber-600 font-medium"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            {language === 'sr' ? '👑 Admin Panel' : '👑 Admin Panel'}
                          </Link>
                        )}
                        <hr className="dark:border-slate-700" />
                        <button
                          onClick={() => {
                            handleLogout();
                            setUserMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 text-red-500"
                        >
                          {t.nav.logout}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/prijava"
                  className="px-4 py-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  {t.nav.login}
                </Link>
                <Link
                  to="/registracija"
                  className="px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
                >
                  {t.nav.register}
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-slate-800 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden py-4 border-t border-slate-800"
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-amber-500 text-slate-900 font-semibold'
                      : 'hover:bg-slate-800'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Token purchase link in mobile menu */}
              {isAuthenticated && (
                <Link
                  to="/kupi-tokene"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-slate-800 text-amber-400"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                  </svg>
                  <span>{language === 'sr' ? 'Kupi tokene' : 'Buy Tokens'}</span>
                  <span className="ml-auto bg-amber-500/20 px-2 py-0.5 rounded text-sm font-medium">{tokenBalance}</span>
                </Link>
              )}

              {/* Language & Theme toggles in mobile menu (visible on small screens where header hides them) */}
              {isAuthenticated && (
                <div className="flex items-center gap-3 px-4 py-3 sm:hidden">
                  <button
                    onClick={() => setLanguage(language === 'sr' ? 'en' : 'sr')}
                    className="px-3 py-1.5 bg-slate-800 rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors"
                  >
                    {language === 'sr' ? 'EN' : 'SR'}
                  </button>
                  <button
                    onClick={toggleTheme}
                    className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    {theme === 'light' ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
